import React, { FC } from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { CoreButton, CoreIconButton } from '../../../_core/components/_ui/core-button';
import { EngagementSurveyDto } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { buttonModalWindowStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { mergeSx } from 'merge-sx';
import { useOpenConfirmModal } from '../../hooks/modal-forms/use-open-confirm-modal-form.hook';
import { useGeneratSurveyQrCodeMutation } from '../../../../store/api/survey.api';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { CopyText } from '../../../_core/constants/icons.constants';
import { useSnackbar } from 'notistack';

export type QrCodeModalProps = CoreModalProps & {
	survey: EngagementSurveyDto;
	submitAction?: Promise<void>;
};

export const QrCodeModal: FC<QrCodeModalProps> = (props) => {
	const [t] = useTranslation();
	const [generateSurveyQrCode] = useGeneratSurveyQrCodeMutation();
	const { survey, submitAction, ...modalProps } = props;
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const featchActionGenerateQrCode = async () => {
		try {
			await generateSurveyQrCode({ surveyId: survey._id }).unwrap();
		} catch (error) {
			console.log(error)
		}
	};

	const handleOpenQrCodeConfirmModal = useOpenConfirmModal({
		featchAction: featchActionGenerateQrCode,
		title: 'Are you sure that you want to generate new QR code?',
		description: 'Old QR code will become inactive',
		submitButtonName: 'Generate new',
		toastText: 'QR code was successfully generated',
	});

	const handleGenerateFirstQrCode = async () => {
		try {
			await featchActionGenerateQrCode();
			showToast('QR code was successfully generated', 'success');
		} catch (error) {
			console.error(error);
			showToast('Something goes wrong', 'error');
		}
	};

	const handleSaveQrCodeAsPng = () => {
		if (survey.qrCode?.qrCode) {
			const link = document.createElement('a');
			link.href = survey.qrCode?.qrCode;
			link.download = 'qrcode.png';
			link.click();
		}
	};

	const warnCopyText = () => {
		showToast('Text is copied', 'info');
	};
	if (!survey) {
		return (
			<CoreModal {...modalProps}>
				<Typography sx={{ p: 2 }}>No survey data available</Typography>
			</CoreModal>
		);
	}

	return (
		<CoreModal {...modalProps} bodySx={{ pt: 0 }} paperSx={{ width: '100%' }}>
			<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
				<Typography variant="h5" sx={mergeSx(mainTitleModalWindowStyles)}>
					QR Code
				</Typography>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
					{survey.qrCode?.qrCode ? (
						<Box sx={{ height: '200px' }}>
							<img src={survey.qrCode?.qrCode} alt="Survey link QR code" style={{ height: '100%' }} />
						</Box>
					) : (
						<Typography sx={{ fontSize: '16px', py: 1 }}>QR code isn't generated yet</Typography>
					)}
					{survey.qrCode?.originalUrl ? (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Link href={survey.qrCode?.originalUrl}>{survey.qrCode?.originalUrl}</Link>
							<CopyToClipboard text={survey.qrCode?.originalUrl ?? ''} onCopy={warnCopyText}>
								<Tooltip title={t('buttons.tooltips.copy')}>
									<CoreIconButton>
										<CopyText />
									</CoreIconButton>
								</Tooltip>
							</CopyToClipboard>
						</Box>
					) : null}
				</Box>

				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						gap: 3,
						mt: 5,
					}}
				>
					<CoreButton
						onClick={survey.qrCode ? handleOpenQrCodeConfirmModal : handleGenerateFirstQrCode}
						variant="secondary"
						sx={buttonModalWindowStyles}
					>
						Generate new
					</CoreButton>
					<CoreButton
						disabled={!survey.qrCode}
						onClick={handleSaveQrCodeAsPng}
						contraModeStyleButton
						sx={mergeSx(buttonModalWindowStyles, { textTransform: 'none' })}
					>
						Save as .PNG
					</CoreButton>
				</Box>
			</Box>
		</CoreModal>
	);
};
